// Mirrored in applications/platform-app/components/web-analytics — change both.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Download } from 'lucide-react';
import type { WebAnalyticsApi } from './api';
import { errorMessage, errorStatus } from './api';
import type { SiteRef, SitesResponse, SummaryResponse, WebDim, Incident, Block, BlockCreate } from './types';
import { DashContext, type BlockPrefill, type DashCtx } from './context';
import { useDashboardState, TAB_IDS, type TabId } from './useDashboardState';
import { useWebQuery } from './useWebQuery';
import { SiteSwitcher, SiteFav } from './SiteSwitcher';
import { RangeBar } from './RangeBar';
import { CustomRangeModal } from './CustomRangeModal';
import { FilterChips } from './FilterChips';
import { ErrorLine, LoadingOverlay, Toast } from './ui';
import { filterDimFor, filterLabel, RUBY } from './format';
import { OverviewTab } from './tabs/OverviewTab';
import { RealtimeTab } from './tabs/RealtimeTab';
import { AcquisitionTab } from './tabs/AcquisitionTab';
import { PagesTab } from './tabs/PagesTab';
import { ConversionsTab } from './tabs/ConversionsTab';
import { AudienceTab } from './tabs/AudienceTab';
import { SecurityTab } from './tabs/SecurityTab';
import { SettingsTab } from './tabs/SettingsTab';
import { BlockModal, DURATION_LABEL } from './modals/BlockModal';
import { IncidentModal } from './modals/IncidentModal';
import { ViewAllModal } from './modals/ViewAllModal';

export interface ChromeProps {
  /** Under the title: the site's domain(s) or the platform blurb. */
  subtitle: string;
  /** Header right side: live pill, site switcher (admin), Export. */
  actions: React.ReactNode;
  /** Tab bar — rendered INSIDE the black header, above the ruby line. */
  tabs: React.ReactNode;
  children: React.ReactNode;
}

export interface WebAnalyticsDashboardProps {
  api: WebAnalyticsApi;
  mode: 'admin' | 'tenant';
  /** Tenant mode: the one site this dashboard shows (t_<tenantId>); hides the switcher. */
  fixedSiteId?: string;
  /** Page chrome (InventoryPage in super-admin, the merchant shell in platform-app). */
  chrome: (p: ChromeProps) => React.ReactNode;
  /** Opens a presigned export URL (download links in export emails). Injectable for tests. */
  openUrl?: (url: string) => void;
}

const TAB_LABEL: Record<TabId, string> = {
  overview: 'Overview', realtime: 'Realtime', acquisition: 'Acquisition', pages: 'Pages',
  conversions: 'Conversions', audience: 'Audience', security: 'Security', settings: 'Settings',
};
const TOAST_MS = 3200;

export const WebAnalyticsDashboard: React.FC<WebAnalyticsDashboardProps> = ({ api, mode, fixedSiteId, chrome, openUrl }) => {
  const tenant = mode === 'tenant';
  const ds = useDashboardState({ defaultSite: tenant ? (fixedSiteId || '') : '_all', fixedSiteId: tenant ? fixedSiteId : undefined });
  const { state } = ds;
  const siteId = state.site;
  const filtered = state.filters.length > 0;

  // ── toast ──
  const [toastText, setToastText] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout>>();
  const toast = useCallback((t: string) => {
    setToastText(t);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastText(null), TOAST_MS);
  }, []);
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  // ── sites ──
  const [sites, setSites] = useState<SiteRef[]>([]);
  const [sitesError, setSitesError] = useState<string | null>(null);
  useEffect(() => {
    let off = false;
    api.sites()
      .then((r: SitesResponse) => { if (!off) { setSites(r.sites || []); setSitesError(null); } })
      .catch((e) => { if (!off) setSitesError(errorMessage(e)); });
    return () => { off = true; };
  }, [api]);
  const site = sites.find((s) => s.siteId === siteId);
  const siteKind = siteId === '_all' ? 'platform' : site?.kind || (siteId === 'rubytill' ? 'company' : 'store');

  // ── loading registry (any query in flight → gem overlay) ──
  const [loadingIds, setLoadingIds] = useState<Set<string>>(() => new Set());
  const reportLoading = useCallback((id: string, on: boolean) => {
    setLoadingIds((prev) => {
      if (prev.has(id) === on) return prev;
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  }, []);

  // ── header summary (live + threat badge + Overview KPIs) ──
  const summary = useWebQuery<SummaryResponse>({
    api, siteId, range: state.range, filters: state.filters, want: 'summary', enabled: !!siteId,
    unfiltered: () => api.summary(siteId, state.range),
  });
  useEffect(() => { reportLoading('summary', summary.loading); }, [summary.loading, reportLoading]);
  const [live, setLive] = useState<number | null>(null);
  useEffect(() => { if (typeof summary.data?.live === 'number') setLive(summary.data.live); }, [summary.data]);
  useEffect(() => { setLive(null); }, [siteId]);

  // ── modals ──
  const [blockFor, setBlockFor] = useState<BlockPrefill | null>(null);
  const [incident, setIncident] = useState<Incident | null>(null);
  const [viewAll, setViewAll] = useState<{ dim: WebDim; title: string } | null>(null);
  const [customOpen, setCustomOpen] = useState(false);
  const [blocksNonce, setBlocksNonce] = useState(0);
  const bumpBlocks = useCallback(() => setBlocksNonce((n) => n + 1), []);

  // ── export download links (?download=<exportId> from the export email) ──
  const handled = useRef<string | null>(null);
  const downloadId = ds.takeParam('download');
  useEffect(() => {
    if (!downloadId || !siteId || handled.current === downloadId) return;
    handled.current = downloadId;
    const open = openUrl || ((u: string) => { window.location.assign(u); });
    api.exportDownload(siteId, downloadId)
      .then((r) => { open(r.url); toast('Your export is downloading'); })
      .catch((e) => {
        const st = errorStatus(e);
        toast(st === 409 ? 'Your export is still being prepared'
          : st === 404 || st === 410 ? 'This export has expired — run it again'
            : errorMessage(e));
      })
      .finally(() => ds.dropParam('download'));
  }, [downloadId, siteId, api, openUrl, toast, ds]);

  const addFilter = ds.addFilter;
  const rowFilter = useCallback((dim: WebDim, value: string) => {
    if (dim === 'site') return () => ds.setSite(value);
    const fd = filterDimFor(dim);
    if (!fd || !value) return undefined;
    return () => {
      const f = { dim: fd, value };
      addFilter(f);
      toast(`Filtered: ${filterLabel(f)}. Every tab now shows only this segment.`);
    };
  }, [addFilter, ds, toast]);

  const ctx: DashCtx = useMemo(() => ({
    api, mode, siteId, site, siteKind, sites, state, filtered, summary,
    setSite: ds.setSite, setTab: ds.setTab, setMetric: ds.setMetric, addFilter, rowFilter, toast,
    openBlock: (p?: BlockPrefill) => setBlockFor(p || {}),
    openIncident: setIncident,
    openViewAll: (dim: WebDim, title: string) => setViewAll({ dim, title }),
    blocksNonce, bumpBlocks, setLive, reportLoading,
  }), [api, mode, siteId, site, siteKind, sites, state, filtered, summary, ds, addFilter, rowFilter, toast, blocksNonce, bumpBlocks, reportLoading]);

  const doExport = async () => {
    try {
      await api.exportCsv(siteId, state.range, state.filters);
      toast("Export started — we'll email you the CSV");
    } catch (e) {
      toast(errorMessage(e));
    }
  };

  const onBlocked = (b: Block, body: BlockCreate) => {
    setBlockFor(null);
    setIncident(null);
    bumpBlocks();
    const where = b.scope === 'all' ? 'all websites' : 'this website';
    toast(`Blocked ${b.target} on ${where} · ${DURATION_LABEL[body.duration]}. Takes effect within a few minutes.`);
  };

  // ── chrome pieces ──
  const openIncidents = summary.data?.threat?.openIncidents || 0;
  const tabs = (
    <nav className="flex items-center gap-1 overflow-x-auto" role="tablist" aria-label="Analytics sections">
      {TAB_IDS.map((t) => {
        const on = state.tab === t;
        return (
          <button
            key={t} type="button" role="tab" aria-selected={on} data-testid={`wa-tab-${t}`}
            onClick={() => ds.setTab(t)}
            className={`px-4 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition min-h-[44px] ${on ? '' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
            style={on ? { background: 'rgba(220,20,60,0.16)', border: '1px solid rgba(220,20,60,0.5)', color: '#fda4af' } : { border: '1px solid transparent' }}
          >
            {TAB_LABEL[t]}
            {t === 'security' && openIncidents > 0 && (
              <span className="ml-1.5 rounded-full px-1.5 py-px text-[10px] text-white" style={{ background: RUBY }}>{openIncidents}</span>
            )}
          </button>
        );
      })}
    </nav>
  );

  const actions = (
    <div className="flex items-center gap-2 flex-wrap max-w-[calc(100vw-32px)]">
      <button
        type="button" onClick={() => ds.setTab('realtime')} title="Open Realtime" data-testid="wa-live"
        className="flex items-center gap-1.5 text-gray-300 text-xs px-2.5 h-11 border border-[#262626] rounded-[10px] bg-transparent"
      >
        <i className="wa-live-dot w-2 h-2 rounded-full bg-green-500 inline-block" />
        <span>{live ?? 0}</span>&nbsp;online now
      </button>
      {tenant ? (
        <div className="h-11 min-w-0 px-3 rounded-[10px] border border-[#333] bg-[#111] text-white flex items-center gap-2.5" data-testid="wa-site-fixed">
          <SiteFav site={site || { siteId, name: '?', kind: 'store' }} />
          <span className="min-w-0"><b className="block text-[13px] truncate">{site?.name || 'Your website'}</b><small className="block text-gray-400 text-[11px]">{site ? `${site.hostnames.length} domain${site.hostnames.length === 1 ? '' : 's'} · your website` : 'your website'}</small></span>
        </div>
      ) : (
        <SiteSwitcher sites={sites} value={siteId} onChange={ds.setSite} error={sitesError} />
      )}
      <button type="button" onClick={doExport} data-testid="wa-export" className="h-11 px-3.5 rounded-[10px] border border-[#333] bg-[#111] text-gray-200 text-xs font-semibold flex items-center gap-1.5">
        <Download size={14} /> Export
      </button>
    </div>
  );

  const subtitle = siteId === '_all'
    ? 'Traffic, conversions and security across every website RubyTill hosts'
    : site?.hostnames.length ? site.hostnames.join(' · ') : site?.name || '';

  const Tab = {
    overview: OverviewTab, realtime: RealtimeTab, acquisition: AcquisitionTab, pages: PagesTab,
    conversions: ConversionsTab, audience: AudienceTab, security: SecurityTab, settings: SettingsTab,
  }[state.tab];
  const busy = loadingIds.size > 0;

  const body = (
    <DashContext.Provider value={ctx}>
      <style>{`@keyframes wa-pulse{70%{box-shadow:0 0 0 7px rgba(34,197,94,0)}100%{box-shadow:0 0 0 0 rgba(34,197,94,0)}}.wa-live-dot{box-shadow:0 0 0 0 rgba(34,197,94,.6);animation:wa-pulse 1.6s infinite}`}</style>
      <div className="relative min-h-[60vh] text-[13px] text-stone-900" data-testid="wa-dashboard">
        <div className="flex gap-2.5 items-center flex-wrap mb-3.5">
          {state.tab !== 'settings' && state.tab !== 'realtime' && (
            <RangeBar
              range={state.range}
              onPreset={(p) => ds.setRange({ range: p, compare: state.range.compare })}
              onCustom={() => setCustomOpen(true)}
              onCompare={ds.setCompare}
              compareDisabled={filtered}
            />
          )}
          <FilterChips filters={state.filters} onRemove={ds.removeFilter} onClear={ds.clearFilters} />
          <span className="flex-1" />
          {state.tab !== 'settings' && state.tab !== 'realtime' && <span className="text-[11px] text-stone-500">Click any row to filter the whole dashboard</span>}
        </div>
        {sitesError && <div className="mb-3"><ErrorLine message={`Couldn't load the website list — ${sitesError}`} /></div>}
        <div className={busy ? 'opacity-25 transition-opacity' : 'transition-opacity'}>
          {siteId ? <Tab key={`${siteId}`} /> : null}
        </div>
        <LoadingOverlay on={busy} />
      </div>
      {blockFor && (
        <BlockModal
          api={api} mode={mode} siteId={siteId} siteName={site?.name || siteId} prefill={blockFor}
          onClose={() => setBlockFor(null)} onCreated={onBlocked}
        />
      )}
      {incident && (
        <IncidentModal
          incident={incident} siteName={sites.find((s) => s.siteId === incident.siteId)?.name || site?.name || incident.siteId}
          onClose={() => setIncident(null)}
          onBlock={(src) => { setIncident(null); setBlockFor({ kind: src.includes('/') ? 'net24' : 'ip', target: src }); }}
        />
      )}
      {viewAll && <ViewAllModal dim={viewAll.dim} title={viewAll.title} onClose={() => setViewAll(null)} />}
      {customOpen && (
        <CustomRangeModal
          initial={state.range.range === 'custom' ? { from: state.range.from, to: state.range.to } : undefined}
          onClose={() => setCustomOpen(false)}
          onApply={(from, to) => { setCustomOpen(false); ds.setRange({ range: 'custom', from, to, compare: state.range.compare }); }}
        />
      )}
      <Toast text={toastText} />
    </DashContext.Provider>
  );

  return <>{chrome({ subtitle, actions, tabs, children: body })}</>;
};

export default WebAnalyticsDashboard;
